import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from './auth.service';
import { WorkflowAction } from './ops.service';
import {
  AppModule,
  MODULE_ACCESS_PERMISSION,
  MODULE_ROLES,
  WORKFLOW_ACTION_ROLES,
  canAccessModule,
  isAssigneeScoped,
  isInvestigatorDesk,
  roleAllowed,
} from '../rbac/rbac.policy';
import { OfficerRole } from '../models/officer.model';

@Injectable({ providedIn: 'root' })
export class RbacService {
  private readonly auth = inject(AuthService);

  readonly role = computed(() => this.auth.officer()?.role ?? null);

  readonly permissionCodes = computed(
    () => this.auth.officer()?.permissionCodes ?? [],
  );

  readonly fullAccess = computed(
    () => this.role() === 'admin' || this.auth.officer()?.fullAccess === true,
  );

  /** Built-in officer or any Admin-created investigative desk. */
  readonly isInvestigator = computed(() => isInvestigatorDesk(this.role()));

  /** Sees only complaints assigned to their user id. */
  readonly isAssigneeScoped = computed(() => isAssigneeScoped(this.role()));

  /**
   * Module visibility from live DB permission matrix (session.permissionCodes).
   * Super Admin always allowed (safety fallback — never lock out admin).
   */
  canModule(module: AppModule): boolean {
    return canAccessModule(
      this.role(),
      this.permissionCodes(),
      module,
      this.fullAccess(),
    );
  }

  canWorkflowAction(action: WorkflowAction): boolean {
    return roleAllowed(this.role(), WORKFLOW_ACTION_ROLES[action]);
  }

  filterWorkflowActions(actions: WorkflowAction[]): WorkflowAction[] {
    return actions.filter((a) => this.canWorkflowAction(a));
  }

  /** Roles that historically could open a module (docs / messaging only). */
  rolesForModule(module: AppModule): OfficerRole[] {
    return MODULE_ROLES[module];
  }

  accessPermissionFor(module: AppModule): string {
    return MODULE_ACCESS_PERMISSION[module];
  }
}
